import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  getPlayer, recordGame, updateUsername, recordDuelWin,
  mergeLocalWithRemote,
} from './data/player';
import {
  getSession, signOut, fetchProfile, syncLocalToBackend,
  saveGame, incrementDuelWins, fetchIncomingChallenges, subscribeIncomingChallenges,
  fetchOpenDuels, subscribeOpenDuels, declineChallenge,
} from './lib/db';
import { supabase, isConfigured } from './lib/supabase';
import { C, CIVS } from './data/constants';

import Splash      from './components/Splash';
import Onboarding  from './components/Onboarding';
import Auth        from './components/Auth';
import Home        from './components/Home';
import Quiz        from './components/Quiz';
import Duel        from './components/Duel';
import Results     from './components/Results';
import Leaderboard from './components/Leaderboard';
import Profile     from './components/Profile';
import Badges      from './components/Badges';
import BadgeDetail from './components/BadgeDetail';


export default function App() {
  const [screen,     setScreen]     = useState('splash');
  const [obSlide,    setObSlide]    = useState(0);
  const [activeTab,  setActiveTab]  = useState('leaderboard');
  const [selCiv,     setSelCiv]     = useState('all');
  const [selDiff,    setSelDiff]    = useState('apprentice');
  const [results,    setResults]    = useState(null);
  const [badgeIndex, setBadgeIndex] = useState(0);
  const [player,     setPlayer]     = useState(() => getPlayer());
  const [user,       setUser]       = useState(null);
  const [pendingChallengeCount, setPendingChallengeCount] = useState(0);
  const [openDuels,        setOpenDuels]        = useState([]);
  const [duelJoinCode,     setDuelJoinCode]     = useState('');
  const [autoCreateDuel,   setAutoCreateDuel]   = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState(null);
  const [initialChallenge, setInitialChallenge] = useState(null);
  const challengeTimerRef = useRef(null);

  const go    = useCallback(s => setScreen(s), []);
  const goTab = useCallback(t => { setScreen(t); setActiveTab(t); }, []);

  // ── Session management ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isConfigured) return;

    // Restore existing session on load
    getSession().then(async session => {
      if (session?.user) {
        setUser(session.user);
        try {
          const profile = await fetchProfile(session.user.id);
          if (profile) {
            const local = getPlayer();
            const merged = mergeLocalWithRemote(local, profile);
            setPlayer(merged);
          }
        } catch {}
      }
    });

    // Keep user state in sync with Supabase auth events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || !isConfigured) { setPendingChallengeCount(0); setPendingChallenge(null); return; }

    const showChallenge = (challenge) => {
      setPendingChallenge(challenge);
      clearTimeout(challengeTimerRef.current);
      challengeTimerRef.current = setTimeout(() => setPendingChallenge(null), 60000);
    };

    fetchIncomingChallenges(user.id).then(list => {
      setPendingChallengeCount(list.length);
      if (list.length > 0) showChallenge(list[0]);
    });

    const unsub = subscribeIncomingChallenges(user.id, (challenge) => {
      setPendingChallengeCount(n => n + 1);
      showChallenge(challenge);
    });

    return () => { unsub(); clearTimeout(challengeTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!isConfigured) return;
    fetchOpenDuels().then(setOpenDuels);
    const unsub = subscribeOpenDuels(
      (d) => setOpenDuels(prev => [d, ...prev.filter(x => x.id !== d.id)].slice(0, 10)),
      (id) => setOpenDuels(prev => prev.filter(x => x.id !== id)),
    );
    return unsub;
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────
  const handleAuthSuccess = useCallback(async () => {
    try {
      const session = await getSession();
      if (!session?.user) { go('home'); return; }
      setUser(session.user);
      const local   = getPlayer();
      const merged  = await syncLocalToBackend(session.user.id, local);
      if (merged) setPlayer(mergeLocalWithRemote(local, merged));
    } catch {}
    go('home');
  }, [go]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    setUser(null);
    goTab('home');
  }, [goTab]);

  // ── Username ──────────────────────────────────────────────────────────────
  const setUsername = useCallback((name) => {
    setPlayer(p => updateUsername(p, name));
  }, []);

  // ── Quiz ──────────────────────────────────────────────────────────────────
  const startQuiz = useCallback(() => { setResults(null); go('quiz'); }, [go]);

  const onQuizFinish = useCallback(async (data) => {
    const correct    = data.answers.filter(a => a.correct).length;
    const prevBadges = new Set(player.earnedBadges || []);

    const updated = recordGame(player, {
      score:      data.score,
      correct,
      total:      data.answers.length,
      civ:        selCiv,
      diff:       selDiff,
      bestStreak: data.bestStreak,
    });

    const newBadges = (updated.earnedBadges || []).filter(b => !prevBadges.has(b));
    setPlayer(updated);
    setResults({ ...data, newBadges });
    go('results');

    if (user && isConfigured) {
      saveGame(user.id, {
        score: data.score, correct, total: data.answers.length,
        civ: selCiv, diff: selDiff, bestStreak: data.bestStreak,
      }).catch(() => {});
    }
  }, [player, selCiv, selDiff, go, user]);

  // ── Duel ──────────────────────────────────────────────────────────────────
  const onDuelResult = useCallback((gameData) => {
    const prevBadges = new Set(player.earnedBadges || []);
    const updated = recordGame(player, gameData);
    const newBadges = (updated.earnedBadges || []).filter(b => !prevBadges.has(b));
    setPlayer(updated);
    setResults({ ...gameData, answers: [], newBadges });

    if (user && isConfigured) {
      saveGame(user.id, gameData).catch(() => {});
    }
  }, [player, user]);

  const onDuelWin = useCallback(() => {
    setPlayer(p => recordDuelWin(p));
    if (user && isConfigured) {
      incrementDuelWins(user.id).catch(() => {});
    }
  }, [user]);

  // ── Challenge notification handlers ──────────────────────────────────────
  const handleChallengeAccept = useCallback(() => {
    if (!pendingChallenge) return;
    clearTimeout(challengeTimerRef.current);
    setInitialChallenge(pendingChallenge);
    setPendingChallenge(null);
    setPendingChallengeCount(0);
    go('duel');
  }, [pendingChallenge, go]);

  const handleChallengeDecline = useCallback(async () => {
    if (!pendingChallenge) return;
    clearTimeout(challengeTimerRef.current);
    await declineChallenge(pendingChallenge.id).catch(() => {});
    setPendingChallenge(null);
    setPendingChallengeCount(n => Math.max(0, n - 1));
  }, [pendingChallenge]);

  // ── Badges / misc ─────────────────────────────────────────────────────────
  const openBadge = useCallback(i => { setBadgeIndex(i); go('badge-detail'); }, [go]);

  return (
    <div className="app-outer">
      <div className="app-shell">
        {screen === 'splash' && (
          <Splash
            onStart={() => { setObSlide(0); go('onboarding'); }}
            onLogin={() => go('auth')}
          />
        )}

        {screen === 'onboarding' && (
          <Onboarding
            slideIndex={obSlide}
            onNext={() => setObSlide(i => i + 1)}
            onBack={() => setObSlide(i => i - 1)}
            onFinish={() => goTab('home')}
          />
        )}

        {screen === 'auth' && (
          <Auth
            initialMode="login"
            onSuccess={handleAuthSuccess}
            onBack={() => go(activeTab || 'home')}
          />
        )}

        {screen === 'home' && (
          <Home
            onPlay={startQuiz}
            onDuel={() => { setAutoCreateDuel(true); go('duel'); }}
            onSignIn={() => go('auth')}
            onJoinOpenDuel={(code) => { setDuelJoinCode(code); go('duel'); }}
            onTab={goTab}
            activeTab={activeTab}
            selCiv={selCiv}
            setSelCiv={setSelCiv}
            selDiff={selDiff}
            setSelDiff={setSelDiff}
            player={player}
            setUsername={setUsername}
            user={user}
            pendingChallengeCount={pendingChallengeCount}
            openDuels={openDuels}
          />
        )}

        {screen === 'quiz' && (
          <Quiz
            civKey={selCiv}
            diffKey={selDiff}
            onFinish={onQuizFinish}
            onExit={() => goTab('home')}
            user={user}
          />
        )}

        {screen === 'duel' && (
          <Duel
            user={user}
            player={player}
            onHome={() => { setDuelJoinCode(''); setInitialChallenge(null); setAutoCreateDuel(false); goTab('home'); }}
            onSaveResult={onDuelResult}
            onDuelWin={onDuelWin}
            initialJoinCode={duelJoinCode}
            initialChallenge={initialChallenge}
            autoCreate={autoCreateDuel}
            initialCiv={selCiv}
            initialDiff={selDiff}
          />
        )}

        {screen === 'results' && (
          <Results
            results={results}
            onPlayAgain={startQuiz}
            onHome={() => goTab('home')}
            player={player}
          />
        )}

        {screen === 'leaderboard' && (
          <Leaderboard
            onTab={goTab}
            activeTab={activeTab}
            player={player}
            user={user}
          />
        )}

        {screen === 'profile' && (
          <Profile
            onTab={goTab}
            activeTab={activeTab}
            player={player}
            user={user}
            onSignIn={() => go('auth')}
            onSignOut={handleSignOut}
          />
        )}

        {screen === 'badges' && (
          <Badges
            onBadge={openBadge}
            onTab={goTab}
            activeTab={activeTab}
            player={player}
          />
        )}

        {screen === 'badge-detail' && (
          <BadgeDetail
            badgeIndex={badgeIndex}
            onBack={() => go('badges')}
            onPlay={startQuiz}
            player={player}
          />
        )}

        {/* Floating challenge notification — shown on any screen except duel */}
        {pendingChallenge && screen !== 'duel' && (() => {
          const civ = CIVS.find(c => c.key === pendingChallenge.civ) || CIVS[0];
          const diffLabel = { apprentice: 'Easy', scholar: 'Medium', sage: 'Hard' }[pendingChallenge.diff] || pendingChallenge.diff;
          return (
            <div style={{
              position: 'absolute', bottom: 76, left: 12, right: 12,
              background: '#fff', border: `2px solid ${C.amber}`,
              borderRadius: 16, padding: '14px 16px',
              boxShadow: '0 6px 24px rgba(0,0,0,.18)',
              zIndex: 200, animation: 'slideUp .25s ease',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ fontSize: 30 }}>{civ.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: C.text }}>
                    ⚔️ {pendingChallenge.host_username || 'Someone'} challenges you!
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                    {civ.label} · <span style={{ fontWeight: 700 }}>{diffLabel}</span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleChallengeDecline} style={{ flex: 1, padding: '10px 0', background: '#F5F0EE', color: C.muted, border: `2px solid ${C.border}`, borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Roboto, sans-serif' }}>
                  Decline
                </button>
                <button onClick={handleChallengeAccept} style={{ flex: 2, padding: '10px 0', background: C.amber, color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Roboto, sans-serif' }}>
                  Accept →
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
